import type React from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCurrentUserRoles } from '../../hooks/useHasRole';
import { Roles } from '../../Types/interfaces';
import { InternshipSummaryTable } from './InternshipSummaryTable';

// Roles that have a summary table (students don't), with their tab labels
const ROLE_LABEL_KEYS: Partial<Record<Roles, string>> = {
  [Roles.CadruDidactic]: 'roles.supervisor',
  [Roles.Administrator]: 'roles.administrator',
  [Roles.OrganizationRepresentative]: 'roles.representativeOrganization',
  [Roles.Tutor]: 'roles.tutor',
  [Roles.DepartmentRepresentative]: 'roles.representativeUniversity',
  [Roles.Dean]: 'roles.dean',
  [Roles.LegalDepartment]: 'roles.legalDepartment',
  [Roles.LaborProtection]: 'roles.laborProtection',
  [Roles.Rector]: 'roles.rector',
  [Roles.UniversityCoordinator]: 'roles.universityCoordonator',
};

/**
 * One summary table per role the user has, except Student. With a single
 * role the table is shown directly; with several, each role gets a tab
 * (tables load when opened).
 */
const SummaryTableSelector: React.FC = () => {
  const { t } = useTranslation();
  const roles = useCurrentUserRoles().filter(
    (role) => ROLE_LABEL_KEYS[role] !== undefined
  );

  if (roles.length === 0) {
    return null;
  }

  if (roles.length === 1) {
    return <InternshipSummaryTable role={roles[0]} />;
  }

  return (
    <Tabs
      items={roles.map((role) => ({
        key: String(role),
        label: t(ROLE_LABEL_KEYS[role] ?? ''),
        children: <InternshipSummaryTable role={role} />,
      }))}
    />
  );
};

export default SummaryTableSelector;
